import * as Yup from 'yup';
import { BonusCustomerCondition, CustomerBonusSettlement, getUnitTranslationId } from '@synergy/models';
import { Box, Divider, Stack } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import {
    PoolButton,
    PoolFormikNumberTextfield,
    PoolFormikTextfield,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTypography,
    StyledSumTableRow,
    StyledSumTableWrapper,
} from '@synergy/components-next';
import { getConditionMemberBonus, getCurrencySymbol } from '@synergy/utils';
import { useParams } from 'react-router-dom';

import {
    BonusSettlementSupplierDetailEditFormValues,
    BonusSettlementSupplierDetailEditFormValuesFieldNames,
} from '../models/bonus-settlement-supplier-detail-edit-form-values';
import { BonusSettlementSupplierDetailFormValues } from '../models/bonus-settlement-supplier-detail-form-values';
import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { bonusSettlementsSupplierDetailSelector } from '../store/bonus-settlements-supplier-detail-selector';
import { defaultCurrencySelector } from '../../../../common/selectors/currency-selectors';
import { updateSinglePoolBonus } from '../store/bonus-settlements-supplier-detail-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface BonusSettlementSupplierDetailEditModalProps {
    open: boolean;
    settlementData: {
        index: number;
        settlement: CustomerBonusSettlement;
    } | null;
    onClose: () => void;
}

const fieldNames = BonusSettlementSupplierDetailEditFormValuesFieldNames;

export const BonusSettlementSupplierDetailEditModal = (props: BonusSettlementSupplierDetailEditModalProps) => {
    const { open: openEditModal, settlementData: editModalData, onClose } = props;
    const { bonusSettlementId, id: supplierBonusSettlementId } = useParams<{ bonusSettlementId: string; id: string }>();
    const bonusSettlementSupplierDetail = useAppSelector(bonusSettlementsSupplierDetailSelector);
    const locale = useAppSelector(bestLocaleSelector);
    const dispatch = useThunkDispatch();
    const supplierDetailFormikContext = useFormikContext<BonusSettlementSupplierDetailFormValues>();
    const intl = useIntl();
    const defaultCurrency = useAppSelector(defaultCurrencySelector);

    if (!editModalData || !bonusSettlementId || !supplierBonusSettlementId || !bonusSettlementSupplierDetail) {
        return null;
    }

    const initialValues: BonusSettlementSupplierDetailEditFormValues = {
        id: editModalData.settlement.id,
        actualBonusValues: editModalData.settlement.bonusConditionValues,
        noteToCustomer: editModalData.settlement.noteToCustomer ?? '',
    };

    const handleSubmit = (
        values: BonusSettlementSupplierDetailEditFormValues,
        formikHelpers: FormikHelpers<BonusSettlementSupplierDetailEditFormValues>
    ) => {
        dispatch(
            updateSinglePoolBonus({
                bonusSettlementId: +bonusSettlementId,
                supplierBonusSettlementId: +supplierBonusSettlementId,
                data: values,
            })
        );

        let totalMemberBonus = 0;

        bonusSettlementSupplierDetail.bonusConditions.forEach(condition => {
            const bonusConditionFormikIndex = values.actualBonusValues.findIndex(
                bcv => bcv.bonusConditionId === condition.bonusCondition.id
            );

            const actualBonus = values.actualBonusValues[bonusConditionFormikIndex].actualBonus;

            totalMemberBonus += getConditionMemberBonus(
                actualBonus || 0,
                editModalData.settlement,
                condition.bonusCondition
            );

            const supplierBonusConditionFormikIndex = supplierDetailFormikContext.values.customerBonusSettlements[
                editModalData.index
            ].bonusConditionValues.findIndex(bcv => bcv.bonusConditionId === condition.bonusCondition.id);

            supplierDetailFormikContext.setFieldValue(
                `customerBonusSettlements.${editModalData.index}.bonusConditionValues.${supplierBonusConditionFormikIndex}`,
                {
                    bonusConditionId: condition.bonusCondition.id,
                    value: values.actualBonusValues[bonusConditionFormikIndex].value,
                    actualBonus,
                }
            );
        });

        const totalActualBonus = values.actualBonusValues.reduce((acc, curr) => acc + (curr?.actualBonus ?? 0), 0);

        supplierDetailFormikContext.setFieldValue(
            `customerBonusSettlements.${editModalData.index}.noteToCustomer`,
            values.noteToCustomer
        );
        supplierDetailFormikContext.setFieldValue(
            `customerBonusSettlements.${editModalData.index}.actualBonus`,
            totalActualBonus
        );
        supplierDetailFormikContext.setFieldValue(
            `customerBonusSettlements.${editModalData.index}.memberBonus`,
            totalMemberBonus
        );
        handleClose(formikHelpers);
    };

    const handleClose = (
        formikProps:
            | FormikProps<BonusSettlementSupplierDetailEditFormValues>
            | FormikHelpers<BonusSettlementSupplierDetailEditFormValues>
    ) => {
        formikProps.resetForm();
        onClose();
    };

    const validationSchema = Yup.object().shape({
        actualBonusValues: Yup.array().of(
            Yup.object().shape({
                actualBonus: Yup.number().typeError('app.error.required').required('app.error.required'),
            })
        ),
        noteToCustomer: Yup.string().max(150, 'app.error.maxLength').required('app.error.required'),
    });

    const { bonusConditions } = bonusSettlementSupplierDetail;

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={validationSchema}
        >
            {formikProps => (
                <PoolModal open={openEditModal && !!editModalData} onClose={() => handleClose(formikProps)}>
                    <PoolModalTitle>
                        <FormattedMessage id="app.bonusSettlements.supplierDetail.edit" />
                    </PoolModalTitle>
                    <PoolModalContent>
                        <Stack gap={3}>
                            <PoolTypography variant="h5">{editModalData.settlement.customerName}</PoolTypography>

                            <StyledSumTableWrapper>
                                <Stack typography="body2" divider={<Divider />}>
                                    {bonusConditions.map((condition: BonusCustomerCondition) => {
                                        const { currency, mode, unit, id: conditionId } = condition.bonusCondition;
                                        const currencySymbol = getCurrencySymbol(currency || defaultCurrency, locale);
                                        const index = formikProps.values.actualBonusValues.findIndex(
                                            bcv => bcv.bonusConditionId === conditionId
                                        );

                                        return (
                                            <StyledSumTableRow key={conditionId} gap={2}>
                                                <Box sx={{ width: '66%', textAlign: 'left' }}>
                                                    <FormattedMessage
                                                        id={`app.bonusSettlementSupplier.bonusSettlementCondition.${mode}`}
                                                        values={{
                                                            mode,
                                                            quantity: condition.bonusCondition.quantity || 1,
                                                            percentage: intl.formatNumber(
                                                                (condition.bonusCondition.percentage ?? 0) * 100,
                                                                {
                                                                    maximumFractionDigits: 2,
                                                                }
                                                            ),
                                                            price: intl.formatNumber(
                                                                condition.bonusCondition.price ?? 0,
                                                                {
                                                                    maximumFractionDigits: 2,
                                                                    minimumFractionDigits: 2,
                                                                }
                                                            ),
                                                            currency: currencySymbol,
                                                            unit:
                                                                unit &&
                                                                intl.formatMessage({
                                                                    id: getUnitTranslationId(unit),
                                                                }),
                                                        }}
                                                    />
                                                    {' *'}
                                                </Box>
                                                <PoolFormikNumberTextfield
                                                    name={`actualBonusValues.${index}.actualBonus`}
                                                    placeholder={intl.formatMessage(
                                                        {
                                                            id: 'app.bonusSettlements.supplierDetail.conditionValuePlaceholder',
                                                        },
                                                        { conditionMode: mode }
                                                    )}
                                                    suffix={` ${currencySymbol}`}
                                                    fixedDecimalScale={true}
                                                    decimalScale={2}
                                                    minDecimalScale={2}
                                                    fullWidth={true}
                                                    sx={{
                                                        '& .MuiInputBase-input': { textAlign: 'right' },
                                                        width: '33%',
                                                    }}
                                                />
                                            </StyledSumTableRow>
                                        );
                                    })}
                                </Stack>

                                <StyledSumTableRow
                                    typography="body1"
                                    isModal={true}
                                    sx={{ paddingBlock: theme => theme.spacing(1.5) }}
                                >
                                    <PoolTypography
                                        typography="body2"
                                        translationId="app.bonusSettlements.supplierDetail.bonusTotalEditModal"
                                    />

                                    <Box sx={{ textAlign: 'right', fontWeight: 600, mr: 2 }}>
                                        <FormattedNumber
                                            value={formikProps.values.actualBonusValues.reduce(
                                                (acc, curr) => acc + (curr?.actualBonus ?? 0),
                                                0
                                            )}
                                            style="currency"
                                            currency={defaultCurrency}
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={2}
                                        />
                                    </Box>
                                </StyledSumTableRow>
                            </StyledSumTableWrapper>

                            <PoolFormikTextfield
                                name={fieldNames.noteToCustomer}
                                label={<FormattedMessage id="app.bonusSettlements.supplierDetail.noteToCustomer" />}
                                required={true}
                                multiline={true}
                                fullWidth={true}
                                minRows={3}
                                helperTextValues={{ value: 150 }}
                                hint={
                                    <FormattedMessage
                                        id="app.bonusSettlements.supplierDetail.noteToCustomerHelperText"
                                        values={{ value: formikProps.values.noteToCustomer?.length ?? 0 }}
                                    />
                                }
                            />
                        </Stack>
                    </PoolModalContent>
                    <PoolModalActions>
                        <PoolButton variant="outlined" onClick={() => handleClose(formikProps)}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>
                        <PoolButton
                            onClick={() => {
                                formikProps.handleSubmit();
                            }}
                        >
                            <FormattedMessage id="app.common.change" />
                        </PoolButton>
                    </PoolModalActions>
                </PoolModal>
            )}
        </Formik>
    );
};
